import React, { useEffect, useState } from "react";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CompactPricingOutcomes } from "@/components/PricingOutcomes";
import { useApp } from "@/context/AppContext";
import { workspaceAccess } from "@/lib/access";
import client from "@/lib/api";
import {
  checkoutAnalyticsParams,
  getGoogleAnalyticsClientId,
  trackPublicEvent,
  trackPublicEventThen,
} from "@/lib/analytics";
import { translate as i18n } from "@/i18n";
import { toast } from "sonner";

const PLAN_KEY = "plan_completo";

export default function WorkspaceAccessDialog({ open, onOpenChange, feature }) {
  const app = useApp() || {};
  const user = app.user;
  const t = app.t || i18n;
  const archived = workspaceAccess(user) === "ARCHIVED";
  const [terms, setTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setTerms(false);
      setTermsError(false);
      setLoading(false);
    }
  }, [open]);

  const checkout = async () => {
    if (!terms) {
      setTermsError(true);
      return toast.error(t("paywall.termsRequired"));
    }
    if (loading) return;
    const analyticsParams = checkoutAnalyticsParams(PLAN_KEY, "workspace_access");
    trackPublicEvent("plan_select", analyticsParams);
    trackPublicEvent("checkout_click", {
      ...analyticsParams,
      auth_state: user ? "authenticated" : "anonymous",
    });
    setLoading(true);
    try {
      const analyticsClientId = await getGoogleAnalyticsClientId();
      const { data } = await client.post("/payments/checkout", {
        lookup_key: PLAN_KEY,
        contractAccepted: true,
        ...(analyticsClientId ? { analytics_client_id: analyticsClientId } : {}),
      });
      trackPublicEventThen("begin_checkout", analyticsParams, () => {
        window.location.href = data.checkout_url;
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || t("paywall.paymentError"));
      setLoading(false);
    }
  };

  const changeOpen = (nextOpen) => {
    if (!loading) onOpenChange?.(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent
        className="flex h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-lg flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:h-auto sm:max-h-[calc(100dvh-2rem)]"
        data-testid="workspace-access-dialog"
      >
        <DialogHeader className="shrink-0 border-b border-slate-200 px-4 pb-3 pt-4 pr-12 text-left sm:px-6 sm:pb-4 sm:pt-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2A9D8F]/10">
              <LockKeyhole className="h-4 w-4 text-[#0F4C5C]" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-head text-lg leading-6 text-slate-900">
                {archived ? t("workspaceAccess.archivedTitle") : t("workspaceAccess.previewTitle")}
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-xs leading-4">
                {feature ? t("workspaceAccess.featureLocked", { feature }) : archived ? t("workspaceAccess.archivedDescription") : t("workspaceAccess.previewDescription")}
              </DialogDescription>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#2A9D8F]/25 bg-[#2A9D8F]/5 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{t("ui.pages_pricing.plan_completo")}</p>
              <p className="text-xs leading-4 text-slate-600">{t("workspaceAccess.pricePromise")}</p>
            </div>
            <p className="shrink-0 font-head text-2xl font-bold text-[#0F4C5C]">15 €</p>
          </div>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3 sm:px-6 sm:py-4"
          data-testid="workspace-access-outcomes-scroll"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#0F4C5C]">
            {t("workspaceAccess.includedTitle")}
          </p>
          <CompactPricingOutcomes />
        </div>

        <div
          className="shrink-0 border-t border-slate-200 bg-white px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pb-4"
          data-testid="workspace-access-checkout-footer"
        >
          <label className="flex items-start gap-2 text-xs leading-4 text-slate-600">
            <Checkbox
              checked={terms}
              onCheckedChange={(checked) => {
                setTerms(checked === true);
                if (checked === true) setTermsError(false);
              }}
              aria-invalid={termsError}
              disabled={loading}
              data-testid="workspace-checkout-terms"
              className="mt-0.5 shrink-0"
            />
            <a
              href="/legal/condiciones"
              target="_blank"
              rel="noreferrer"
              data-testid="workspace-checkout-terms-link"
              className={`underline ${termsError ? "text-red-600" : "text-[#0F4C5C]"}`}
            >
              {t("ui.pages_paywall.acceptPurchaseTerms")}
            </a>
          </label>
          <Button
            onClick={checkout}
            disabled={loading}
            data-testid="workspace-checkout-confirm"
            className="mt-3 min-h-11 w-full rounded-xl bg-[#0F4C5C] hover:bg-[#0c3f4c]"
          >
            {loading ? t("ui.pages_pricing.redirigiendo") : t("workspaceAccess.unlock")}
          </Button>
          <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5 text-[#2A9D8F]" />{t("paywall.securePayment")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
